import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'

/* The add/edit modal, shared by every surface that creates or changes a record.
 *
 * There is exactly ONE of these because there is exactly one editing primitive:
 * the sheet — bottom-anchored on touch, a centred dialog on desktop. A second
 * modal implementation would mean a second set of dismissal rules, and a user
 * who has learned that Escape closes the cost-line editor must not discover
 * that the client editor keeps it open.
 *
 * What this adds over Sheet itself is the FORM: a real <form> element, so
 * Enter submits from any field and the browser's own validation and autofill
 * behave; a footer whose primary action is the submit button; and a pending
 * state that disables both actions while a mutation is in flight rather than
 * closing optimistically and reopening on failure.
 *
 * Cancel is always present and always the escape hatch. Destructive confirmation
 * is NOT this component's job — a sheet that both edits and deletes makes the
 * dangerous action reachable by the same muscle memory as the safe one.
 *
 * The UNSAVED-CHANGES guard below is not an exception to that rule, and the
 * distinction is worth stating because the two look alike. A delete
 * confirmation stands in front of an action the user asked for; this stands in
 * front of a LOSS they did not — an Escape keypress, a stray click on the
 * scrim. Nothing is destroyed by confirming a delete twice, and a sheet full of
 * typing is destroyed by closing it once. It belongs here rather than in each
 * sheet because every dismissal path a sheet has — the scrim, the close button,
 * Escape, Cancel — arrives through this one `onClose`, and a guard written at a
 * call site would have to cover four of them and would cover three.
 */

export interface FormSheetProps {
  open: boolean
  onClose: () => void
  /** Names the record being created or edited, e.g. "New client". */
  title: string
  onSubmit: () => void
  /** Label for the primary action. Defaults to the shared "Save". */
  submitLabel?: string
  /** Blocks submission while a mutation is in flight. */
  pending?: boolean
  /** Disables submission — an unmet requirement, not a transient state. */
  disabled?: boolean
  /** Record-level error, shown ABOVE the fields (EXPERIENCE.md § State Patterns). */
  error?: string
  /** A standing limitation rather than a failure — "this cannot be saved yet".
   *  Neutral, not negative: nothing went wrong, so nothing should look red. */
  notice?: string
  /** Whether the draft has moved since the sheet opened — `useFormDraft`'s
   *  fifth return value. When true, every dismissal path asks before it
   *  discards; when false or absent the sheet closes silently, because a
   *  confirmation over an untouched form is a dialog that teaches people to
   *  dismiss dialogs without reading them. */
  dirty?: boolean
  children: ReactNode
}

/* Base UI: none needed — this composes Sheet.tsx, which is the `@base-ui/react`
   dialog primitive; FormSheet itself only adds the form element, the footer
   layout and the dirty-state guard. */
export function FormSheet({
  open,
  onClose,
  title,
  onSubmit,
  submitLabel,
  pending,
  disabled,
  error,
  notice,
  dirty,
  children,
}: FormSheetProps) {
  const { t } = useTranslation()

  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /* Reset by KEY during render, the pattern useFormDraft and DeleteRow both
     document: the sheet keeps its markup mounted, so nothing unmounts to clear
     this, and an effect would paint one frame with the question already on
     screen. Without it a sheet that was closed while the question stood would
     reopen still asking it. */
  const [confirmedFor, setConfirmedFor] = useState(open)
  if (confirmedFor !== open) {
    setConfirmedFor(open)
    setConfirmingDiscard(false)
  }

  /* The single funnel every dismissal passes through: the scrim, the header's
     close button, Escape, and the footer's Cancel. */
  const requestClose = () => {
    // A second dismissal while the question stands answers it the safe way.
    // Escape is the case that matters: having pressed it once and been asked,
    // pressing it again must not be inert — and it must not discard either.
    if (confirmingDiscard) {
      setConfirmingDiscard(false)
      return
    }
    if (dirty) {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  /* One id PER SHEET, not one id for the kit. The footer's submit button
     reaches its form by `form={id}`, which the browser resolves with
     getElementById — first match in the document wins. Sheet now closes
     through Base UI's dialog with `keepMounted`, so a closed sheet's markup
     stays in the DOM until its exit transition is torn down, and two sheets
     mounted at once (a picker that hands off to an editor is the ordinary
     case) would both answer to a shared constant id. The submit would then
     land on whichever one React rendered first — usually the closed one, and
     silently: no error, no save. */
  const formId = useId()

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    // The sheet lives inside the page, and on the detail surfaces that page can
    // itself sit inside a form. Stopping propagation keeps an inner submit from
    // reaching an outer one and saving two records at once.
    event.preventDefault()
    event.stopPropagation()
    if (pending || disabled) return
    onSubmit()
  }

  return (
    <Sheet
      open={open}
      onClose={requestClose}
      title={title}
      footer={
        /* The primary action is full-width on touch, where it is the thumb's
           target, and shrinks beside Cancel on desktop. Both live in the footer
           so the commit point is in the same place on every sheet. */
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            size="lg"
            onClick={requestClose}
            disabled={pending}
            className="sm:w-auto"
          >
            {t('common.actions.cancel')}
          </Button>
          {/* Submits the form by id rather than by nesting: the footer is a
              sibling of the form inside Sheet's own layout, not a descendant. */}
          <Button
            type="submit"
            form={formId}
            size="lg"
            disabled={pending || disabled}
            className="sm:w-auto"
          >
            {pending
              ? t('common.form.saving')
              : (submitLabel ?? t('common.actions.save'))}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate>
        {/* A record-level failure sits above the record it concerns; a field's
            own error sits beside the field. Never a summary of both. */}
        {notice ? (
          // Not role="alert": a standing limitation is not an event, and
          // announcing it as one interrupts a screen reader mid-sentence.
          <p className="bg-surface-sunken text-text-muted mb-3.5 rounded-md px-3 py-2 text-[12.5px] leading-snug">
            {notice}
          </p>
        ) : null}
        {error ? (
          <p
            role="alert"
            className="bg-negative-soft text-negative mb-3.5 rounded-md px-3 py-2 text-[12.5px] leading-snug"
          >
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-3.5 pt-1">{children}</div>
      </form>

      {/* The unsaved-changes question, over the sheet it is asking about.
          Absolutely positioned INSIDE the sheet rather than opened as a second
          dialog beside it, and that is the decision worth defending here.

          A nested dialog would be a second modal whose own dismissal rules
          (Escape, its scrim) sit on top of the ones the user just triggered,
          and the two would compete for the same keypress — Escape would have
          to mean "keep editing" one layer up and "close the sheet" the layer
          below, which is exactly the ambiguity a guard exists to remove. This
          covers the panel it belongs to, moves with it, and leaves the rest of
          the page reachable, which is what `modal={false}` already promises
          everywhere else in this kit.

          `role="alertdialog"` and the focused Keep button: a guard nobody hears
          is a guard that discards the work of anyone not looking at the screen. */}
      {confirmingDiscard ? (
        <div className="absolute inset-0 z-10 flex items-end justify-center rounded-[inherit] bg-[var(--overlay-scrim)] p-4 sm:items-center">
          <div
            role="alertdialog"
            aria-modal="false"
            aria-label={t('common.unsaved.title')}
            className="bg-surface w-full max-w-sm rounded-[var(--radius-lg)] p-4 shadow-[var(--shadow-overlay)]"
          >
            <h3 className="text-text-heading text-[13.5px] leading-snug font-medium">
              {t('common.unsaved.title')}
            </h3>
            <p className="text-text-body mt-1.5 text-[12.5px] leading-snug">
              {t('common.unsaved.body')}
            </p>

            {/* Keep first and autofocused, discard second and styled as the
                dangerous action it is. A reflexive Enter must not be the thing
                that loses the draft. */}
            <div className="mt-3.5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setConfirmingDiscard(false)
                  onClose()
                }}
                className="sm:w-auto"
              >
                {t('common.unsaved.discard')}
              </Button>
              <Button
                autoFocus
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDiscard(false)}
                className="sm:w-auto"
              >
                {t('common.unsaved.keep')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Sheet>
  )
}

